<template>
  <div
    class="frame-renderer"
    ref="container"
    :style="containerStyle"
    @mousemove="onMouseMove"
    @mousedown="onMouseDown"
    @mouseup="onMouseUp"
    @mouseleave="onMouseLeave"
  >
    <canvas ref="canvas" width="400" height="300"></canvas>

    <!-- Dimension / hover tooltip overlay -->
    <div
      v-if="tooltipVisible"
      class="frame-renderer__tooltip"
      :style="tooltipStyle"
    >
      {{ tooltipText }}
    </div>

    <!-- Dimension annotations (HTML overlay positioned via 3D projection) -->
    <div v-if="showDimensions" class="frame-renderer__dimensions">
      <div
        v-if="widthLabel"
        class="frame-renderer__dim-label frame-renderer__dim-label--width"
        :style="widthLabelStyle"
      >
        {{ widthLabel }}
      </div>
      <div
        v-if="heightLabel"
        class="frame-renderer__dim-label frame-renderer__dim-label--height"
        :style="heightLabelStyle"
      >
        {{ heightLabel }}
      </div>
    </div>

    <!-- Loading overlay -->
    <div v-if="loading" class="frame-renderer__loading">
      <div class="spinner-border spinner-border-sm text-secondary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three'
import { debounce } from 'lodash'
import {
  loadTexture,
  loadFrameTextures,
  createArtworkPlane,
  createMiteredFrame,
  createHangHardware,
  createStretcherBars,
  createSubstrateBackPanel,
  updateMiteredRailGeometry,
  updateBackLegGeometry,
  createMatBorder,
  createMultiMatBorder,
  createResizeHandles,
  createDimensionLines,
  computeOuterDimensions,
  applyArtworkTextureCover,
  inches,
} from '@/utils/frameGeometry.js'

export default {
  name: 'FrameRenderer',

  props: {
    /** Substrate name (e.g. 'Print', 'Canvas') - affects backing paper, stretchers */
    substrate: {
      type: String,
      default: '',
    },
    /** Artwork width in inches */
    artWidth: {
      type: [Number, String],
      default: 16,
    },
    /** Artwork height in inches */
    artHeight: {
      type: [Number, String],
      default: 20,
    },
    /** URL of the artwork image */
    artworkImageUrl: {
      type: String,
      default: null,
    },
    /**
     * Frame option extra_data from the available-options API.
     * Uses top_image for texturing. Expected shape: { top_image, width, rabbet, ... }
     * Pass null for no frame.
     */
    frameData: {
      type: Object,
      default: null,
    },
    /**
     * Mat option extra_data.
     * Expected shape: { color, ... }
     * Pass null for no mat.
     */
    matData: {
      type: Object,
      default: null,
    },
    /** Multi-mat: array of { color } for Mat 1, Mat 2, Mat 3 (outer to inner) */
    matDataArray: {
      type: Array,
      default: null,
    },
    /** Mat border width in inches. Object { top, bottom, left, right } or single number. */
    matWidth: {
      type: [Number, String, Object],
      default: 2,
    },
    /** Multi-mat: per-mat widths [{ top, bottom, left, right }, ...] */
    matWidthsArray: {
      type: Array,
      default: null,
    },
    /** Container max width in CSS (e.g. '100%', '600px') */
    maxWidth: {
      type: String,
      default: '100%',
    },
    /** Container max height in CSS */
    maxHeight: {
      type: String,
      default: '500px',
    },
    /** Whether interactive features (hover, resize) are enabled */
    interactive: {
      type: Boolean,
      default: true,
    },
    /** Background color of the scene */
    backgroundColor: {
      type: String,
      default: '#f0f0f0',
    },
    /**
     * Hang option data for back-of-frame hardware visualization.
     * Expected shape: { name: 'wire' | 'security hardware' | ... }
     */
    hangData: {
      type: Object,
      default: null,
    },
    /** Stretcher bar depth in inches (Canvas only). When > 0 and substrate is Canvas, show stretchers. */
    stretcherDepth: {
      type: [Number, String],
      default: 0,
    },
    /** Canvas mount style: "Gallery Wrapped", "Frame", or "Float Frame" */
    canvasMount: {
      type: String,
      default: '',
    },
    /** Extra data from Float Frame option (has width for frame face) */
    floatFrameData: {
      type: Object,
      default: null,
    },
    /** Substrate back panel: { depth, color, roughness, metalness } for Aluminum, Acrylic, etc. */
    substrateBackData: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      tooltipVisible: false,
      tooltipText: '',
      tooltipX: 0,
      tooltipY: 0,
      showDimensions: false,
      widthLabel: '',
      heightLabel: '',
      widthLabelPos: { x: 0, y: 0 },
      heightLabelPos: { x: 0, y: 0 },
      isDragging: false,
      dragHandle: null,
      dragStartPos: null,
      dragStartDims: null,
      dragWidth: null,
      dragHeight: null,
      flipRotation: 0,
      flipAnimating: false,
    }
  },

  /**
   * Three.js objects are stored directly on the instance (not in data())
   * to avoid Vue reactivity overhead and the _-prefix reservation rule.
   */
  created() {
    this.threeRenderer = null
    this.threeScene = null
    this.threeCamera = null
    this.threeRaycaster = null
    this.threeMouse = null
    this.animFrameId = null
    this.artworkMesh = null
        this.frameGroup = null
        this.matGroup = null
        this.stretcherGroup = null
        this.substrateBackMesh = null
    this.handlesGroup = null
    this.dimGroup = null
    this.sceneContentGroup = null
    this.hangHardwareGroup = null
    this.builtDims = null
    this.hoveredObject = null
    this._textureCache = null // { artworkUrl, artworkTex, frameKey, frameTextures }
    this.pendingDragDims = null
    this.dragRafId = null
    this.lastEmittedDims = null // { width, height } - avoid duplicate emits
  },

  computed: {
    containerStyle() {
      return {
        width: '100%',
        maxWidth: this.maxWidth,
        height: 'calc(100vh - 300px)',
        maxHeight: this.maxHeight,
        position: 'relative',
        overflow: 'hidden',
      }
    },
    tooltipStyle() {
      return {
        left: this.tooltipX + 'px',
        top: this.tooltipY + 'px',
      }
    },
    widthLabelStyle() {
      return {
        left: this.widthLabelPos.x + 'px',
        top: this.widthLabelPos.y + 'px',
      }
    },
    heightLabelStyle() {
      return {
        left: this.heightLabelPos.x + 'px',
        top: this.heightLabelPos.y + 'px',
      }
    },
    parsedArtWidth() {
      if (this.isDragging && this.dragWidth != null) return this.dragWidth
      return parseFloat(this.artWidth) || 16
    },
    parsedArtHeight() {
      if (this.isDragging && this.dragHeight != null) return this.dragHeight
      return parseFloat(this.artHeight) || 20
    },
    frameWidth() {
      return this.frameData ? parseFloat(this.frameData.width) || 0 : 0
    },
    /** Frame depth: match frame width up to 2", default to 1.5" */
    frameDepth() {
      const w = this.frameWidth
      return w > 0 ? Math.min(2, w) : 1.5
    },
    frameRabbet() {
      return this.frameData ? parseFloat(this.frameData.rabbet) || 0 : 0
    },
    parsedMatWidth() {
      const hasMat = this.matData || (this.matDataArray && this.matDataArray.length > 0)
      if (!hasMat) return { top: 0, bottom: 0, left: 0, right: 0 }
      if (typeof this.matWidth === 'object' && this.matWidth !== null) {
        return {
          top: parseFloat(this.matWidth.top) || 0,
          bottom: parseFloat(this.matWidth.bottom) || 0,
          left: parseFloat(this.matWidth.left) || 0,
          right: parseFloat(this.matWidth.right) || 0,
        }
      }
      const w = parseFloat(this.matWidth) || 0
      return { top: w, bottom: w, left: w, right: w }
    },
    /** Max mat width for any side — used for frame offset calculations */
    maxMatWidth() {
      const m = this.parsedMatWidth
      return Math.max(m.top, m.bottom, m.left, m.right)
    },
    hasMatWidth() {
      const m = this.parsedMatWidth
      return m.top > 0 || m.bottom > 0 || m.left > 0 || m.right > 0
    },
  },

  watch: {
    artWidth() { if (!this.isDragging) this.debouncedRebuild() },
    artHeight() { if (!this.isDragging) this.debouncedRebuild() },
    artworkImageUrl() { this.debouncedRebuild() },
    substrate() { this.debouncedRebuild() },
    canvasMount() { this.debouncedRebuild() },
    floatFrameData: { handler() { this.debouncedRebuild() }, deep: true },
    substrateBackData: { handler() { this.debouncedRebuild() }, deep: true },
    frameData: { handler() { this.debouncedRebuild() }, deep: true },
    matData: { handler() { this.debouncedRebuild() }, deep: true },
    matDataArray: { handler() { this.debouncedRebuild() }, deep: true },
    matWidthsArray: { handler() { this.debouncedRebuild() }, deep: true },
    matWidth() { this.debouncedRebuild() },
    hangData: { handler() { this.debouncedRebuild() }, deep: true },
    stretcherDepth() { this.debouncedRebuild() },
    backgroundColor() { this.updateBackground() },
  },

  mounted() {
    this.debouncedRebuild = debounce(() => this.rebuildScene(), 150)
    this.rebuildVersion = 0

    // Wait for layout to settle before initializing Three.js
    this.$nextTick(() => {
      this.initThree()
      this.rebuildScene()
      this.startRenderLoop()
    })

    // Listen for window resize
    this.boundOnResize = () => this.onResize()
    window.addEventListener('resize', this.boundOnResize)

    this.boundHandleDrag = (e) => this.handleDrag(e)
    this.boundEndResizeDrag = () => this.endResizeDrag()
  },

  beforeDestroy() {
    if (this.boundHandleDrag) {
      document.removeEventListener('mousemove', this.boundHandleDrag)
      document.removeEventListener('mouseup', this.boundEndResizeDrag)
    }
    if (this.dragRafId != null) {
      cancelAnimationFrame(this.dragRafId)
      this.dragRafId = null
    }
    // Cancel pending debounced rebuilds
    if (this.debouncedRebuild && this.debouncedRebuild.cancel) {
      this.debouncedRebuild.cancel()
    }
    this.stopRenderLoop()
    if (this.boundOnResize) {
      window.removeEventListener('resize', this.boundOnResize)
    }
    if (this.threeRenderer) {
      this.threeRenderer.dispose()
      this.threeRenderer.forceContextLoss()
      this.threeRenderer = null
    }
  },

  methods: {
    // ------------------------------------------------------------------
    // Initialization
    // ------------------------------------------------------------------

    initThree() {
      const container = this.$refs.container
      const canvas = this.$refs.canvas
      if (!container || !canvas) return

      const rect = container.getBoundingClientRect()
      const width = Math.max(rect.width, 200)
      const height = Math.max(rect.height, 200)

      // Renderer - create one WebGL context per component lifetime
      this.threeRenderer = new THREE.WebGLRenderer({
        canvas,
        antialias: true,
        preserveDrawingBuffer: true, // needed for toDataURL export
        alpha: false,
      })
      this.threeRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
      this.threeRenderer.setSize(width, height)
      this.threeRenderer.outputColorSpace = THREE.SRGBColorSpace

      // Scene
      this.threeScene = new THREE.Scene()
      this.updateBackground()

      // Camera (orthographic for head-on view)
      this.threeCamera = new THREE.OrthographicCamera(
        -width / 2, width / 2,
        height / 2, -height / 2,
        0.1, 1000
      )
      this.threeCamera.position.set(0, 0, 100)
      this.threeCamera.lookAt(0, 0, 0)

      // Lighting
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.85)
      this.threeScene.add(ambientLight)

      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.4)
      directionalLight.position.set(2, 3, 20)
      this.threeScene.add(directionalLight)

      // Raycaster for interactions
      this.threeRaycaster = new THREE.Raycaster()
      this.threeMouse = new THREE.Vector2()
    },

    updateBackground() {
      if (this.threeScene) {
        this.threeScene.background = new THREE.Color(this.backgroundColor)
      }
    },

    // ------------------------------------------------------------------
    // Scene building
    // ------------------------------------------------------------------

    async rebuildScene(opts = {}) {
      if (!this.threeScene) return

      const silent = opts.silent === true
      const version = ++this.rebuildVersion
      if (!silent) this.loading = true

      const isCanvas = String(this.substrate || '').toLowerCase() === 'canvas'
      const canvasMount = String(this.canvasMount || '').trim()
      const isGalleryWrapped = isCanvas && /gallery\s*wrapped/i.test(canvasMount)
      const isFloatFrame = isCanvas && /float\s*frame/i.test(canvasMount)

      const effectiveFrameData = isFloatFrame && this.floatFrameData
        ? this.floatFrameData
        : this.frameData
      const frameKey = effectiveFrameData?.top_image || this.frameData?.top_image || ''

      let artworkTex
      let textures

      try {
        // Use cached textures for silent rebuilds (fast path during drag)
        if (silent && this._textureCache) {
          const c = this._textureCache
          if (c.artworkUrl === this.artworkImageUrl && c.frameKey === frameKey) {
            artworkTex = c.artworkTex
            textures = c.frameTextures
          }
        }
        if (!artworkTex) {
          artworkTex = await loadTexture(this.artworkImageUrl)
          if (version !== this.rebuildVersion) return
        }
        if (!textures && effectiveFrameData && (parseFloat(effectiveFrameData.width) || parseFloat(this.frameWidth))) {
          textures = await loadFrameTextures(effectiveFrameData)
          if (version !== this.rebuildVersion) return
        }
        if (!textures) textures = { front: null, side: null, top: null }

        this._textureCache = {
          artworkUrl: this.artworkImageUrl,
          artworkTex,
          frameKey,
          frameTextures: textures,
        }

        const mw = isGalleryWrapped ? { top: 0, bottom: 0, left: 0, right: 0 } : this.parsedMatWidth
        const floatFrameGap = isFloatFrame ? (parseFloat(this.floatFrameData?.gap) || 0.25) : 0

        const wfBack = this.substrateBackData
        const isWindowFilm = /window\s*film/i.test(this.substrate || '')
        const artworkOpts = isWindowFilm && wfBack ? {
          side: wfBack.useBackface ? THREE.DoubleSide : THREE.FrontSide,
          opacity: wfBack.opacity ?? 1,
          transparent: (wfBack.opacity ?? 1) < 1,
          perforated: wfBack.perforated ?? false,
        } : {}
        const newArtworkMesh = createArtworkPlane(
          this.parsedArtWidth,
          this.parsedArtHeight,
          artworkTex,
          artworkOpts
        )

        let newMatGroup = null
        const matArr = this.matDataArray || (this.matData ? [this.matData] : [])
        const widthsArr = this.matWidthsArray || (this.hasMatWidth ? [mw] : [])
        if (!isGalleryWrapped && matArr.length > 0 && widthsArr.length > 0) {
          if (matArr.length > 1) {
            newMatGroup = createMultiMatBorder({
              artWidth: this.parsedArtWidth,
              artHeight: this.parsedArtHeight,
              mats: matArr,
              widths: widthsArr,
            })
          } else {
            newMatGroup = createMatBorder({
              artWidth: this.parsedArtWidth,
              artHeight: this.parsedArtHeight,
              matTop: mw.top,
              matBottom: mw.bottom,
              matLeft: mw.left,
              matRight: mw.right,
              color: (matArr[0]?.color || this.matData?.color || '#FFFFFF'),
            })
          }
        }

        let newFrameGroup = null
        const stretcherD = Number(this.stretcherDepth) || 0
        const effectiveFrameWidth = isFloatFrame && this.floatFrameData
          ? (parseFloat(this.floatFrameData.width) || this.frameWidth)
          : this.frameWidth
        const showFrame = !isGalleryWrapped && effectiveFrameData && effectiveFrameWidth > 0
        if (showFrame) {
          // Canvas frame depth matches stretcher assembly depth (75% of bar width) when stretchers present
          const stretcherActualDepth = stretcherD > 0 ? stretcherD * 0.75 : 0
          const frameDepth = isCanvas && stretcherD > 0
            ? stretcherActualDepth
            : Math.min(2, effectiveFrameWidth)
          const innerW = this.parsedArtWidth + mw.left + mw.right + (isFloatFrame ? 2 * floatFrameGap : 0)
          const innerH = this.parsedArtHeight + mw.top + mw.bottom + (isFloatFrame ? 2 * floatFrameGap : 0)
          newFrameGroup = createMiteredFrame({
            artWidth: innerW,
            artHeight: innerH,
            frameWidth: effectiveFrameWidth,
            rabbet: effectiveFrameData.rabbet != null ? parseFloat(effectiveFrameData.rabbet) : this.frameRabbet,
            depth: frameDepth,
            textures,
            isFloatFrame,
          })
          const offsetX = (mw.right - mw.left) / 2
          const offsetY = (mw.top - mw.bottom) / 2
          // Canvas: align frame front with canvas front (both at z=0); Print: frame front at z=d
          const frameZ = isCanvas ? -inches(frameDepth) : 0
          newFrameGroup.position.set(offsetX, offsetY, frameZ)
        }

        let newSubstrateBackMesh = null
        const isMural = /mural/i.test(this.substrate || '')
        const isWindowFilmOpaque = /window\s*film/i.test(this.substrate || '') && this.substrateBackData?.depth > 0
        if (this.substrateBackData && this.substrateBackData.depth > 0 && (!showFrame || isMural || isWindowFilmOpaque)) {
          const sb = this.substrateBackData
          const border = sb.border || 0
          const subW = this.parsedArtWidth + 2 * border
          const subH = this.parsedArtHeight + 2 * border
          newSubstrateBackMesh = createSubstrateBackPanel(subW, subH, sb)
        }

        let newStretcherGroup = null
        const depth = stretcherD
        if (isCanvas && depth > 0) {
          newStretcherGroup = createStretcherBars(
            this.parsedArtWidth,
            this.parsedArtHeight,
            depth
          )
        }

        let newHandlesGroup = null
        let newHangHardwareGroup = null
        const innerW = this.parsedArtWidth + mw.left + mw.right
        const innerH = this.parsedArtHeight + mw.top + mw.bottom
        if (this.hangData) {
          const hangName = this.hangData.name || this.hangData.value || ''
          // Backmost depth: stretchers (if present), else substrate back, else frame depth for canvas, else default
          const frameD = showFrame ? (isCanvas && stretcherD > 0 ? stretcherD * 0.75 : Math.min(2, effectiveFrameWidth)) : 0
          const substrateD = this.substrateBackData?.depth || 0
          const backDepth = isCanvas && stretcherD > 0 ? stretcherD * 0.75
            : (isCanvas && frameD > 0 ? frameD : substrateD > 0 ? substrateD : 0.1)
          // Canvas with stretchers: hardware must stay within stretcher bars (art size). Otherwise use inner (art + mat).
          const hwWidth = isCanvas && stretcherD > 0 ? this.parsedArtWidth : innerW
          const hwHeight = isCanvas && stretcherD > 0 ? this.parsedArtHeight : innerH
          newHangHardwareGroup = createHangHardware({
            innerWidth: hwWidth,
            innerHeight: hwHeight,
            hangType: hangName,
            substrate: this.substrate || '',
            backDepth,
          })
        }

        let newDimGroup = null
        if (this.interactive) {
          newHandlesGroup = createResizeHandles(
            this.parsedArtWidth,
            this.parsedArtHeight
          )
          const dimArtW = isFloatFrame ? this.parsedArtWidth + 2 * floatFrameGap : this.parsedArtWidth
          const dimArtH = isFloatFrame ? this.parsedArtHeight + 2 * floatFrameGap : this.parsedArtHeight
          const substrateBorder = this.substrateBackData?.border ?? 0
          const { outerWidth, outerHeight } = computeOuterDimensions({
            artWidth: dimArtW,
            artHeight: dimArtH,
            matTop: mw.top,
            matBottom: mw.bottom,
            matLeft: mw.left,
            matRight: mw.right,
            frameWidth: showFrame ? effectiveFrameWidth : 0,
            rabbet: this.frameRabbet,
            substrateBorder,
          })
          newDimGroup = createDimensionLines(
            this.parsedArtWidth,
            this.parsedArtHeight,
            (showFrame ? effectiveFrameWidth : 0) + this.maxMatWidth,
            outerWidth,
            outerHeight
          )
        }

        // Swap: remove old, add new in one sync block
        const contentParent = this.sceneContentGroup || this.threeScene
        const toRemove = []
        contentParent.traverse((obj) => {
          if (obj !== this.sceneContentGroup && obj.userData?.type) toRemove.push(obj)
        })
        toRemove.forEach((obj) => {
          if (obj.parent) obj.parent.remove(obj)
          if (obj.geometry) obj.geometry.dispose()
          if (obj.material) {
            if (Array.isArray(obj.material)) {
              obj.material.forEach(m => m.dispose())
            } else {
              obj.material.dispose()
            }
          }
        })

        if (!this.sceneContentGroup) {
          this.sceneContentGroup = new THREE.Group()
          this.sceneContentGroup.userData = { type: 'content' }
          this.threeScene.add(this.sceneContentGroup)
        }
        this.sceneContentGroup.scale.set(1, 1, 1)

        const frameDepthUsed = showFrame
          ? (isCanvas && stretcherD > 0 ? stretcherD * 0.75 : Math.min(2, effectiveFrameWidth))
          : 0
        const hwW = isCanvas && stretcherD > 0 ? this.parsedArtWidth : innerW
        const hwH = isCanvas && stretcherD > 0 ? this.parsedArtHeight : innerH
        const substrateBorder = this.substrateBackData?.border || 0
        this.builtDims = {
          artW: this.parsedArtWidth,
          artH: this.parsedArtHeight,
          substrateBorder,
          innerW,
          innerH,
          backPanelBuiltW: innerW + 2,
          backPanelBuiltH: innerH + 2,
          hwBuiltW: hwW,
          hwBuiltH: hwH,
          mw: { ...mw },
          isFloatFrame,
          floatFrameGap,
          isCanvas,
          frameDepthUsed,
          hasStretchers: isCanvas && stretcherD > 0,
        }

        this.artworkMesh = newArtworkMesh
        this.matGroup = newMatGroup
        this.frameGroup = newFrameGroup
        this.substrateBackMesh = newSubstrateBackMesh
        this.stretcherGroup = newStretcherGroup
        this.hangHardwareGroup = newHangHardwareGroup
        this.handlesGroup = newHandlesGroup
        this.dimGroup = newDimGroup

        this.sceneContentGroup.add(this.artworkMesh)
        if (this.matGroup) this.sceneContentGroup.add(this.matGroup)
        if (this.stretcherGroup) this.sceneContentGroup.add(this.stretcherGroup)
        if (this.substrateBackMesh) this.sceneContentGroup.add(this.substrateBackMesh)
        if (this.frameGroup) this.sceneContentGroup.add(this.frameGroup)
        if (this.hangHardwareGroup) this.sceneContentGroup.add(this.hangHardwareGroup)
        if (this.handlesGroup) this.sceneContentGroup.add(this.handlesGroup)
        if (this.dimGroup) this.sceneContentGroup.add(this.dimGroup)

        if (!silent) this.fitCamera()
      } catch (err) {
        console.error('FrameRenderer: failed to build scene', err)
      } finally {
        if (!silent) this.loading = false
      }
    },

    /**
     * Update scene geometry scales and positions for new dimensions.
     * No geometry creation - GPU-friendly, just transforms.
     */
    updateSceneDimensions(artW, artH) {
      if (!this.builtDims) return
      const b = this.builtDims
      const mw = this.parsedMatWidth
      const innerW = artW + mw.left + mw.right
      const innerH = artH + mw.top + mw.bottom

      if (this.artworkMesh) {
        this.artworkMesh.scale.set(artW / b.artW, artH / b.artH, 1)
        const tex = this.artworkMesh.material?.map
        if (tex) applyArtworkTextureCover(tex, artW / artH)
        const alphaMap = this.artworkMesh.material?.alphaMap
        if (alphaMap) {
          const rx = artW * 4
          const ry = artH * 4
          alphaMap.repeat.set(rx, ry)
          alphaMap.offset.set(0.5 * (1 - rx), 0.5 * (1 - ry))
        }
      }

      if (this.matGroup) {
        if (this.matGroup.userData.multi) {
          this.matGroup.scale.set(artW / b.artW, artH / b.artH, 1)
        } else {
          const fullW = artW + mw.left + mw.right
          const cx = (mw.right - mw.left) / 2
          const d = inches(0.0625)
          const aw = inches(artW)
          const ah = inches(artH)
          const byLabel = {
            'Mat (top)': { scale: [inches(fullW), inches(mw.top), 1], pos: [inches(cx), ah / 2 + inches(mw.top) / 2, d / 2] },
            'Mat (bottom)': { scale: [inches(fullW), inches(mw.bottom), 1], pos: [inches(cx), -(ah / 2 + inches(mw.bottom) / 2), d / 2] },
            'Mat (left)': { scale: [inches(mw.left), ah, 1], pos: [-(aw / 2 + inches(mw.left) / 2), 0, d / 2] },
            'Mat (right)': { scale: [inches(mw.right), ah, 1], pos: [aw / 2 + inches(mw.right) / 2, 0, d / 2] },
          }
          this.matGroup.traverse((obj) => {
            if (obj.geometry?.parameters && obj.userData?.type === 'mat-piece') {
              const p = obj.geometry.parameters
              const cfg = byLabel[obj.userData.label]
              if (cfg) {
                obj.scale.set(cfg.scale[0] / p.width, cfg.scale[1] / p.height, cfg.scale[2] / p.depth)
                obj.position.set(...cfg.pos)
              }
            }
          })
        }
      }

      if (this.frameGroup) {
        const frameInnerW = b.isFloatFrame ? innerW + 2 * (b.floatFrameGap || 0.25) : innerW
        const frameInnerH = b.isFloatFrame ? innerH + 2 * (b.floatFrameGap || 0.25) : innerH
        const fw = b.isFloatFrame && this.floatFrameData
          ? (parseFloat(this.floatFrameData.width) || this.frameWidth)
          : this.frameWidth
        const frameDepthVal = b.frameDepthUsed ?? Math.min(2, fw)
        const d = inches(frameDepthVal) / 2
        const iw = inches(frameInnerW)
        const ih = inches(frameInnerH)
        const offsetX = inches((mw.right - mw.left) / 2)
        const offsetY = inches((mw.top - mw.bottom) / 2)
        const frameZ = b.isCanvas ? -inches(frameDepthVal) : 0
        this.frameGroup.position.set(offsetX, offsetY, frameZ)

        this.frameGroup.traverse((obj) => {
          if (obj.userData?.type === 'frame-rail') {
            const pos = obj.userData.position
            if (pos === 'top' || pos === 'bottom') {
              updateMiteredRailGeometry(obj, frameInnerW, fw, pos, frameDepthVal, b.isFloatFrame)
              obj.position.set(0, (pos === 'top' ? 1 : -1) * ih / 2, d)
              obj.scale.y = pos === 'bottom' ? -1 : 1
            } else {
              updateMiteredRailGeometry(obj, frameInnerH, fw, pos, frameDepthVal, b.isFloatFrame)
              obj.position.set((pos === 'right' ? 1 : -1) * iw / 2, 0, d)
            }
          } else if (b.isFloatFrame && obj.userData?.type === 'frame-back-leg') {
            updateBackLegGeometry(obj, frameInnerW, frameInnerH, fw, frameDepthVal)
          }
        })

        this.builtDims.innerW = innerW
        this.builtDims.innerH = innerH
      }

      if (this.stretcherGroup) {
        this.stretcherGroup.scale.set(artW / b.artW, artH / b.artH, 1)
      }

      if (this.substrateBackMesh) {
        const sb = b.substrateBorder || 0
        if (sb > 0) {
          const denomW = b.artW + 2 * sb
          const denomH = b.artH + 2 * sb
          this.substrateBackMesh.scale.set((artW + 2 * sb) / denomW, (artH + 2 * sb) / denomH, 1)
        } else {
          this.substrateBackMesh.scale.set(artW / b.artW, artH / b.artH, 1)
        }
      }

      if (this.hangHardwareGroup && b.hwBuiltW != null && b.hwBuiltH != null) {
        const scaleX = (b.hasStretchers ? artW : innerW + 2) / (b.hasStretchers ? b.hwBuiltW : b.backPanelBuiltW)
        const scaleY = (b.hasStretchers ? artH : innerH + 2) / (b.hasStretchers ? b.hwBuiltH : b.backPanelBuiltH)
        this.hangHardwareGroup.scale.set(scaleX, scaleY, 1)
      }

      if (this.handlesGroup) {
        const aw = inches(artW)
        const ah = inches(artH)
        const corners = [
          { x: -aw / 2, y: ah / 2 },
          { x: aw / 2, y: ah / 2 },
          { x: -aw / 2, y: -ah / 2 },
          { x: aw / 2, y: -ah / 2 },
        ]
        let i = 0
        this.handlesGroup.traverse((obj) => {
          if (obj.userData?.type === 'resize-handle') {
            obj.position.set(corners[i].x, corners[i].y, 0.5)
            i++
          }
        })
      }
    },

    /**
     * Adjust the orthographic camera so the entire framed piece fits
     * within the container with some padding.
     */
    fitCamera() {
      if (!this.threeCamera || !this.threeRenderer) return

      const mw = this.parsedMatWidth
      const substrateBorder = this.substrateBackData?.border ?? 0
      const { outerWidth, outerHeight } = computeOuterDimensions({
        artWidth: this.parsedArtWidth,
        artHeight: this.parsedArtHeight,
        matTop: mw.top,
        matBottom: mw.bottom,
        matLeft: mw.left,
        matRight: mw.right,
        frameWidth: this.frameWidth,
        rabbet: this.frameRabbet,
        substrateBorder,
      })

      const container = this.$refs.container
      if (!container) return
      const rect = container.getBoundingClientRect()
      const containerW = rect.width || 400
      const containerH = rect.height || 400
      const aspect = containerW / containerH

      // Add padding (5%) for a tighter fit
      const padding = 1.05
      const sceneW = inches(outerWidth) * padding
      const sceneH = inches(outerHeight) * padding

      let halfW, halfH
      if (sceneW / sceneH > aspect) {
        // Scene is wider relative to container
        halfW = sceneW / 2
        halfH = halfW / aspect
      } else {
        // Scene is taller
        halfH = sceneH / 2
        halfW = halfH * aspect
      }

      this.threeCamera.left = -halfW
      this.threeCamera.right = halfW
      this.threeCamera.top = halfH
      this.threeCamera.bottom = -halfH
      this.threeCamera.updateProjectionMatrix()

      this.threeRenderer.setSize(containerW, containerH)
    },

    /** Animate camera from current bounds to fitted bounds over duration ms */
    animateFitCamera(duration = 2000) {
      if (!this.threeCamera || !this.$refs.container) return

      const mw = this.parsedMatWidth
      const substrateBorder = this.substrateBackData?.border ?? 0
      const { outerWidth, outerHeight } = computeOuterDimensions({
        artWidth: this.parsedArtWidth,
        artHeight: this.parsedArtHeight,
        matTop: mw.top,
        matBottom: mw.bottom,
        matLeft: mw.left,
        matRight: mw.right,
        frameWidth: this.frameWidth,
        rabbet: this.frameRabbet,
        substrateBorder,
      })

      const rect = this.$refs.container.getBoundingClientRect()
      const aspect = (rect.width || 400) / (rect.height || 400)
      const padding = 1.05
      const sceneW = inches(outerWidth) * padding
      const sceneH = inches(outerHeight) * padding

      let targetLeft, targetRight, targetTop, targetBottom
      if (sceneW / sceneH > aspect) {
        const halfW = sceneW / 2
        targetLeft = -halfW
        targetRight = halfW
        targetTop = halfW / aspect
        targetBottom = -targetTop
      } else {
        const halfH = sceneH / 2
        targetTop = halfH
        targetBottom = -halfH
        targetLeft = -halfH * aspect
        targetRight = -targetLeft
      }

      const startLeft = this.threeCamera.left
      const startRight = this.threeCamera.right
      const startTop = this.threeCamera.top
      const startBottom = this.threeCamera.bottom
      const startTime = performance.now()

      const animate = (now) => {
        const t = Math.min((now - startTime) / duration, 1)
        const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2

        this.threeCamera.left = startLeft + (targetLeft - startLeft) * eased
        this.threeCamera.right = startRight + (targetRight - startRight) * eased
        this.threeCamera.top = startTop + (targetTop - startTop) * eased
        this.threeCamera.bottom = startBottom + (targetBottom - startBottom) * eased
        this.threeCamera.updateProjectionMatrix()

        if (t < 1) requestAnimationFrame(animate)
      }
      requestAnimationFrame(animate)
    },

    // ------------------------------------------------------------------
    // Render loop
    // ------------------------------------------------------------------

    startRenderLoop() {
      const render = () => {
        this.animFrameId = requestAnimationFrame(render)
        if (this.threeRenderer && this.threeScene && this.threeCamera) {
          if (this.sceneContentGroup) {
            this.sceneContentGroup.rotation.y = this.flipRotation
          }
          this.threeRenderer.render(this.threeScene, this.threeCamera)
        }
      }
      render()
    },

    stopRenderLoop() {
      if (this.animFrameId) {
        cancelAnimationFrame(this.animFrameId)
        this.animFrameId = null
      }
    },

    /** Animate flip to show back of frame (180° Y rotation). */
    flipToBack() {
      if (this.flipAnimating) return
      this.flipAnimating = true
      const duration = 2000
      const startVal = this.flipRotation
      const targetVal = Math.PI
      const startTime = performance.now()

      const animate = (now) => {
        const t = Math.min((now - startTime) / duration, 1)
        const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2
        this.flipRotation = startVal + (targetVal - startVal) * eased
        if (t < 1) requestAnimationFrame(animate)
        else {
          this.flipAnimating = false
          this.$emit('flipped', { showingBack: true })
        }
      }
      requestAnimationFrame(animate)
    },

    /** Animate flip to show front of frame (0° Y rotation). */
    flipToFront() {
      if (this.flipAnimating) return
      this.flipAnimating = true
      const duration = 2000
      const startVal = this.flipRotation
      const targetVal = 0
      const startTime = performance.now()

      const animate = (now) => {
        const t = Math.min((now - startTime) / duration, 1)
        const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2
        this.flipRotation = startVal + (targetVal - startVal) * eased
        if (t < 1) requestAnimationFrame(animate)
        else {
          this.flipAnimating = false
          this.$emit('flipped', { showingBack: false })
        }
      }
      requestAnimationFrame(animate)
    },

    /** Toggle between front and back view. */
    toggleFlip() {
      const showingBack = this.flipRotation > Math.PI / 2
      if (showingBack) this.flipToFront()
      else this.flipToBack()
    },

    // ------------------------------------------------------------------
    // Interaction handlers
    // ------------------------------------------------------------------

    getMouseNDC(event) {
      const rect = this.$refs.canvas.getBoundingClientRect()
      return new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      )
    },

    onMouseMove(event) {
      if (!this.interactive || !this.threeRaycaster || !this.threeCamera) return

      const ndc = this.getMouseNDC(event)
      this.threeRaycaster.setFromCamera(ndc, this.threeCamera)

      // Dragging is handled by document mousemove (tracks beyond canvas)
      if (this.isDragging) return

      // Collect all meshes for raycasting
      const objects = []
      this.threeScene.traverse((obj) => {
        if (obj.isMesh && obj.userData.type) {
          objects.push(obj)
        }
      })

      const intersects = this.threeRaycaster.intersectObjects(objects)

      if (this.hoveredObject) {
        this.onUnhover(this.hoveredObject)
      }

      if (intersects.length > 0) {
        const hit = intersects[0].object
        this.hoveredObject = hit
        this.onHover(hit, event)
        this.updateHandlesVisibility(hit)
      } else {
        this.tooltipVisible = false
        this.showDimensions = false
        this.$refs.container.style.cursor = 'default'
        this.hoveredObject = null
        this.updateHandlesVisibility(null)
      }
    },

    onHover(mesh, event) {
      const data = mesh.userData
      if (!data) return

      const rect = this.$refs.container.getBoundingClientRect()
      this.tooltipX = event.clientX - rect.left + 12
      this.tooltipY = event.clientY - rect.top - 8

      if (data.type === 'resize-handle') {
        this.$refs.container.style.cursor = this.getCursorForCorner(data.corner)
        mesh.material.opacity = 0.95
        this.tooltipText = 'Drag to resize'
        this.tooltipVisible = true
        return
      }

      this.$refs.container.style.cursor = 'default'

      if (data.type === 'artwork') {
        this.tooltipText = `${this.parsedArtWidth}" × ${this.parsedArtHeight}"`
        this.tooltipVisible = true
        this.showDimensionLabels()
      } else if (data.type === 'frame-rail') {
        this.tooltipText = `Frame: ${data.widthInches}" wide`
        this.tooltipVisible = true
      } else if (data.type === 'mat-piece') {
        this.tooltipText = `Mat: ${data.widthInches}" border`
        this.tooltipVisible = true
      } else {
        this.tooltipVisible = false
      }
    },

    onUnhover() {
      // Handle visibility is managed by updateHandlesVisibility
    },

    /** Show/hide resize handles based on hover state. Handles visible only when hovering framed image. */
    updateHandlesVisibility(hoveredMesh) {
      if (!this.handlesGroup) return
      const show = this.isDragging || (hoveredMesh && (
        hoveredMesh.userData?.type === 'artwork' ||
        hoveredMesh.userData?.type === 'frame-rail' ||
        hoveredMesh.userData?.type === 'mat-piece' ||
        hoveredMesh.userData?.type === 'resize-handle'
      ))
      const baseOpacity = show ? 0.6 : 0
      const hoveredHandle = hoveredMesh?.userData?.type === 'resize-handle' ? hoveredMesh : null
      this.handlesGroup.traverse((obj) => {
        if (obj.isMesh && obj.userData?.type === 'resize-handle') {
          obj.material.opacity = (obj === hoveredHandle) ? 0.95 : baseOpacity
        }
      })
    },

    showDimensionLabels() {
      if (!this.threeCamera || !this.threeRenderer) return

      this.showDimensions = true
      const mw = this.parsedMatWidth
      const substrateBorder = this.substrateBackData?.border ?? 0
      const { outerWidth, outerHeight } = computeOuterDimensions({
        artWidth: this.parsedArtWidth,
        artHeight: this.parsedArtHeight,
        matTop: mw.top,
        matBottom: mw.bottom,
        matLeft: mw.left,
        matRight: mw.right,
        frameWidth: this.frameWidth,
        rabbet: this.frameRabbet,
        substrateBorder,
      })
      this.widthLabel = `${outerWidth}"`
      this.heightLabel = `${outerHeight}"`

      const outerW = inches(outerWidth)
      const outerH = inches(outerHeight)
      const labelOffset = 0.5

      // Project 3D points to screen (position labels at outer edges of framed piece)
      const widthMid = new THREE.Vector3(0, -outerH / 2 - labelOffset, 0)
      const heightMid = new THREE.Vector3(outerW / 2 + labelOffset, 0, 0)

      const rect = this.$refs.container.getBoundingClientRect()
      const w = rect.width
      const h = rect.height

      widthMid.project(this.threeCamera)
      this.widthLabelPos = {
        x: (widthMid.x * 0.5 + 0.5) * w,
        y: (-widthMid.y * 0.5 + 0.5) * h,
      }

      heightMid.project(this.threeCamera)
      this.heightLabelPos = {
        x: (heightMid.x * 0.5 + 0.5) * w,
        y: (-heightMid.y * 0.5 + 0.5) * h,
      }
    },

    getCursorForCorner(corner) {
      const cursors = {
        'top-left': 'nwse-resize',
        'top-right': 'nesw-resize',
        'bottom-left': 'nesw-resize',
        'bottom-right': 'nwse-resize',
      }
      return cursors[corner] || 'pointer'
    },

    onMouseDown(event) {
      if (!this.interactive || !this.hoveredObject) return
      const data = this.hoveredObject.userData
      if (data.type !== 'resize-handle') return

      if (this.debouncedRebuild?.cancel) this.debouncedRebuild.cancel()
      this.isDragging = true
      this.dragHandle = data.corner
      this.dragStartPos = { x: event.clientX, y: event.clientY }
      this.dragStartDims = {
        width: this.parsedArtWidth,
        height: this.parsedArtHeight,
      }
      document.addEventListener('mousemove', this.boundHandleDrag)
      document.addEventListener('mouseup', this.boundEndResizeDrag)
      document.body.style.cursor = this.getCursorForCorner(data.corner)
      document.body.style.userSelect = 'none'
      event.preventDefault()
    },

    onMouseUp() {
      this.endResizeDrag()
    },

    endResizeDrag() {
      if (!this.isDragging) return
      document.removeEventListener('mousemove', this.boundHandleDrag)
      document.removeEventListener('mouseup', this.boundEndResizeDrag)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      this.isDragging = false
      this.dragHandle = null
      this.dragStartPos = null
      this.dragStartDims = null
      this.dragWidth = null
      this.dragHeight = null
      // Flush any pending RAF so final dimensions are applied/emitted before rebuild
      if (this.dragRafId != null) {
        cancelAnimationFrame(this.dragRafId)
        this.dragRafId = null
        if (this.pendingDragDims) {
          this.updateSceneDimensions(this.pendingDragDims.width, this.pendingDragDims.height)
          this.$emit('resize', this.pendingDragDims)
          this.pendingDragDims = null
        }
      }
      this.$nextTick(() => {
        this.rebuildScene()
        this.animateFitCamera(2000)
      })
    },

    onMouseLeave() {
      this.tooltipVisible = false
      this.showDimensions = false
      if (this.hoveredObject) {
        this.onUnhover(this.hoveredObject)
        this.hoveredObject = null
      }
      this.updateHandlesVisibility(null)
      if (!this.isDragging) this.$refs.container.style.cursor = 'default'
    },

    handleDrag(event) {
      if (!this.dragStartPos || !this.dragStartDims) return

      const dx = event.clientX - this.dragStartPos.x
      const dy = event.clientY - this.dragStartPos.y

      // Convert pixel delta to inch delta (use canvas rect to match NDC/raycasting)
      const canvas = this.$refs.canvas
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      const cameraWidth = this.threeCamera.right - this.threeCamera.left
      const cameraHeight = this.threeCamera.top - this.threeCamera.bottom
      const trackFactor = 2
      const pxToInchX = (cameraWidth / rect.width) * trackFactor
      const pxToInchY = (cameraHeight / rect.height) * trackFactor

      let newWidth = this.dragStartDims.width
      let newHeight = this.dragStartDims.height

      const corner = this.dragHandle
      const dxInches = dx * pxToInchX
      const dyInches = dy * pxToInchY

      if (corner === 'top-right' || corner === 'bottom-right') {
        newWidth += dxInches
      } else {
        newWidth -= dxInches
      }
      if (corner === 'bottom-left' || corner === 'bottom-right') {
        newHeight += dyInches
      } else {
        newHeight -= dyInches
      }

      newWidth = Math.max(1, Math.round(newWidth * 4) / 4)
      newHeight = Math.max(1, Math.round(newHeight * 4) / 4)

      this.dragWidth = newWidth
      this.dragHeight = newHeight
      if (this.dimGroup) this.dimGroup.visible = false

      // RAF-throttle: batch mousemoves into one update per frame (avoids 4 ExtrudeGeometry allocs per mousemove)
      this.pendingDragDims = { width: newWidth, height: newHeight }
      if (this.dragRafId == null) {
        this.dragRafId = requestAnimationFrame(() => this.applyPendingDragDims())
      }
    },

    applyPendingDragDims() {
      this.dragRafId = null
      const d = this.pendingDragDims
      this.pendingDragDims = null
      if (!d) return
      this.updateSceneDimensions(d.width, d.height)
      // Skip emit if unchanged (reduces Vue reactivity churn during drag)
      if (!this.lastEmittedDims || this.lastEmittedDims.width !== d.width || this.lastEmittedDims.height !== d.height) {
        this.lastEmittedDims = d
        this.$emit('resize', { width: d.width, height: d.height })
      }
    },

    // ------------------------------------------------------------------
    // Resize handling
    // ------------------------------------------------------------------

    onResize() {
      this.fitCamera()
    },

    // ------------------------------------------------------------------
    // Export
    // ------------------------------------------------------------------

    /**
     * Export the current render as a PNG data URL.
     * @param {number} [scale=2] - Resolution multiplier
     * @returns {string} data URL
     */
    exportDataURL(scale = 2) {
      if (!this.threeRenderer || !this.threeScene || !this.threeCamera) return null

      const container = this.$refs.container
      const rect = container.getBoundingClientRect()
      const w = Math.round(rect.width * scale)
      const h = Math.round(rect.height * scale)

      // Temporarily resize renderer for high-res export
      this.threeRenderer.setSize(w, h)
      this.threeRenderer.render(this.threeScene, this.threeCamera)
      const dataURL = this.threeRenderer.domElement.toDataURL('image/png')

      // Restore original size
      this.threeRenderer.setSize(rect.width, rect.height)

      return dataURL
    },

    /**
     * Export as a Blob (for upload or download).
     * @param {number} [scale=2]
     * @returns {Promise<Blob>}
     */
    async exportBlob(scale = 2) {
      const dataURL = this.exportDataURL(scale)
      if (!dataURL) return null
      const res = await fetch(dataURL)
      return res.blob()
    },

    /**
     * Trigger a browser download of the rendered image.
     * @param {string} [filename='frame-render.png']
     * @param {number} [scale=2]
     */
    downloadImage(filename = 'frame-render.png', scale = 2) {
      const dataURL = this.exportDataURL(scale)
      if (!dataURL) return
      const link = document.createElement('a')
      link.href = dataURL
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
}
</script>

<style scoped>
.frame-renderer {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  min-height: 200px;
}

.frame-renderer canvas {
  display: block;
  max-width: 100%;
  max-height: 100%;
}

.frame-renderer__tooltip {
  position: absolute;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  font-size: 0.75rem;
  padding: 3px 8px;
  border-radius: 3px;
  white-space: nowrap;
  z-index: 10;
  transform: translateY(-100%);
}

.frame-renderer__dimensions {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 5;
}

.frame-renderer__dim-label {
  position: absolute;
  font-size: 0.7rem;
  font-weight: 600;
  color: #1976D2;
  background: rgba(255,255,255,0.85);
  padding: 1px 5px;
  border-radius: 2px;
  transform: translate(-50%, -50%);
  white-space: nowrap;
}

.frame-renderer__loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  z-index: 20;
}
</style>
